import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useV2Api } from '../../../hooks/useV2Api'
import { friendlyErrorMessage } from '../../../utils/friendlyError'
import './V2Shipments.css'

const STATUS_LABELS = {
  PENDING_QUOTE: 'Pending quote',
  QUOTED: 'Quoted',
  QUOTE_CONFIRMED: 'Confirmed',
  BOOKED: 'Booked',
  IN_TRANSIT: 'In transit',
  DELIVERED: 'Delivered'
}

const STATUS_COLOR = {
  PENDING_QUOTE: '#8F8F85',
  QUOTED: '#2B2560',
  QUOTE_CONFIRMED: '#5C7A54',
  BOOKED: '#5C7A54',
  IN_TRANSIT: '#8F432F',
  DELIVERED: '#23201C'
}

function formatMoney(n, currency = 'USD') {
  if (n === null || n === undefined) return '—'
  const num = typeof n === 'string' ? parseFloat(n) : n
  return num.toLocaleString('en-US', { style: 'currency', currency, maximumFractionDigits: 0 })
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDateTime(d) {
  if (!d) return '—'
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

function reliabilityPct(score) {
  const n = typeof score === 'string' ? parseFloat(score) : score
  return Math.max(0, Math.min(100, Math.round((n || 0) * 100)))
}

export default function V2ShipmentDetail() {
  const navigate = useNavigate()
  const { id } = useParams()
  const api = useV2Api()

  const [shipment, setShipment] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [errorFallback, setErrorFallback] = useState(null)
  const [requestingQuotes, setRequestingQuotes] = useState(false)
  const [banner, setBanner] = useState(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null); setErrorFallback(null)
    try {
      const res = await api.request(`/api/v2/shipments/${id}`)
      setShipment(res?.data || null)
    } catch (err) {
      setError(err); setErrorFallback('Failed to load shipment')
    } finally {
      setLoading(false)
    }
  }, [api, id])

  useEffect(() => { load() }, [load])

  const handleRequestQuotes = async () => {
    if (!confirm('Send quote requests to all active providers who match this shipment type?')) return
    setBanner(null); setError(null); setErrorFallback(null); setRequestingQuotes(true)
    try {
      const res = await api.request(`/api/v2/shipments/${id}/request-quotes`, { method: 'POST' })
      const count = res?.data?.providersContacted?.length || 0
      setBanner({ kind: 'ok', text: `Quote requests queued for ${count} provider${count === 1 ? '' : 's'}.` })
      await load()
    } catch (err) {
      setError(err); setErrorFallback('Failed to request quotes')
    } finally {
      setRequestingQuotes(false)
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem', opacity: 0.6 }}>Loading shipment…</div>
  }

  if (error && !shipment) {
    return (
      <div>
        <button className="v2-btn v2-btn--outline" onClick={() => navigate('/shipments')}
                style={{ marginBottom: 12 }}>&larr; Back to shipments</button>
        <div style={{
          padding: 16, borderRadius: 8, border: '1px solid #8B3A3A',
          background: 'rgba(139,58,58,0.08)', color: '#8B3A3A'
        }}>
          <strong>Couldn't load shipment.</strong>
          <div style={{ marginTop: 6, fontSize: 14 }}>{friendlyErrorMessage(error, errorFallback)}</div>
        </div>
      </div>
    )
  }

  if (!shipment) return null

  const isTruck = shipment.quote_type === 'domestic_trucking'
  const quotes = shipment.quotes || []
  const events = shipment.events || []
  const routeLabel = isTruck
    ? `${[shipment.pickup_city, shipment.pickup_state].filter(Boolean).join(', ') || 'Trucking'} → ${(shipment.destination_address || '').slice(0, 60) || '—'}`
    : `${shipment.origin_port || '—'} → ${shipment.destination_port || '—'}`

  return (
    <div>
      <div className="v2-page-header">
        <div>
          <button className="v2-btn v2-btn--outline"
                  onClick={() => navigate('/shipments')}
                  style={{ marginBottom: 12, padding: '6px 14px', fontSize: 12 }}>
            &larr; Back to shipments
          </button>
          <h1 className="v2-page-title">{routeLabel}</h1>
          <p className="v2-page-subtitle">
            {shipment.id} · {shipment.commodity || 'No commodity set'} ·{' '}
            <span className="v2-badge" style={{
              background: `${STATUS_COLOR[shipment.status] || '#8F8F85'}14`,
              color: STATUS_COLOR[shipment.status] || '#8F8F85'
            }}>{STATUS_LABELS[shipment.status] || shipment.status}</span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="v2-btn v2-btn--outline"
                  onClick={handleRequestQuotes}
                  disabled={requestingQuotes}>
            {requestingQuotes ? 'Sending…' : 'Request more quotes'}
          </button>
          {quotes.length >= 2 && (
            <button className="v2-btn v2-btn--primary"
                    onClick={() => navigate(`/shipments/${shipment.id}/compare`)}>
              Compare {quotes.length} quotes
            </button>
          )}
        </div>
      </div>

      {banner && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 6, fontSize: 13,
          background: 'rgba(92,122,84,0.12)', color: '#5C7A54', border: '1px solid #5C7A54'
        }}>{banner.text}</div>
      )}
      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 6, fontSize: 13,
          background: 'rgba(139,58,58,0.08)', color: '#8B3A3A', border: '1px solid #8B3A3A'
        }}>{friendlyErrorMessage(error, errorFallback)}</div>
      )}

      <div className="v2-detail-grid">
        {/* Shipment details */}
        <div className="v2-detail-section">
          <div className="v2-detail-section-header">
            <span className="v2-detail-section-title">Shipment details</span>
          </div>
          <div className="v2-detail-section-body">
            <DetailRow label="Type" value={isTruck ? 'Domestic trucking' : 'Ocean freight'} />
            {isTruck ? (
              <>
                <DetailRow label="Pickup" value={[shipment.pickup_city, shipment.pickup_state, shipment.pickup_zip].filter(Boolean).join(', ') || '—'} />
                <DetailRow label="Delivery" value={shipment.destination_address || '—'} />
                <DetailRow label="Truck type" value={(shipment.truck_type || '—').replace('_', ' ')} />
                <DetailRow label="Weight" value={shipment.total_weight_lbs ? `${shipment.total_weight_lbs} lbs` : '—'} />
                <DetailRow label="Delivery deadline" value={formatDateTime(shipment.delivery_deadline)} />
              </>
            ) : (
              <>
                <DetailRow label="Origin" value={shipment.origin_port || '—'} />
                <DetailRow label="Destination" value={`${shipment.destination_port || '—'}${shipment.destination_address ? ' · ' + shipment.destination_address : ''}`} />
                <DetailRow label="Container" value={`${shipment.container_count || 1}× ${shipment.container_type || '—'}`} />
                <DetailRow label="Weight" value={shipment.total_weight ? `${shipment.total_weight} kg` : '—'} />
              </>
            )}
            <DetailRow label="Commodity" value={shipment.commodity || '—'} />
            <DetailRow label="Cargo ready" value={formatDate(shipment.cargo_ready_date)} />
            <DetailRow label="Requested delivery" value={formatDate(shipment.requested_delivery_date)} />
            <DetailRow label="Created" value={formatDateTime(shipment.created_at)} />
            {shipment.confirmed_provider_name && (
              <DetailRow label="Confirmed provider" value={shipment.confirmed_provider_name} />
            )}
          </div>
        </div>

        {/* Quotes */}
        <div className="v2-detail-section">
          <div className="v2-detail-section-header">
            <span className="v2-detail-section-title">
              Quotes ({quotes.length})
            </span>
          </div>
          <div className="v2-detail-section-body">
            {quotes.length === 0 ? (
              <div style={{
                padding: '1.5rem 0.5rem', textAlign: 'center',
                color: '#8F8F85', fontSize: 13
              }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
                No quotes received yet. Providers usually respond within 4-24 hours.
              </div>
            ) : (
              <div className="v2-detail-quote-list">
                {quotes.map((q) => (
                  <div key={q.id}
                       className={`v2-detail-quote-card${q.selected ? ' v2-detail-quote-card--recommended' : ''}`}
                       onClick={() => navigate(`/shipments/${shipment.id}/quotes/${q.id}`)}>
                    <div className="v2-detail-quote-top">
                      <span className="v2-detail-quote-provider">
                        {q.selected && (
                          <span className="v2-badge" style={{
                            background: 'rgba(92,122,84,0.1)', color: '#5C7A54',
                            marginRight: 8, fontSize: 10
                          }}>Selected</span>
                        )}
                        {q.provider_name}
                      </span>
                      <span className="v2-detail-quote-cost">
                        {formatMoney(q.total_cost, q.currency)}
                      </span>
                    </div>
                    <div className="v2-detail-quote-meta">
                      <span>Status: {q.status}</span>
                      {q.transit_days && <span>{q.transit_days} days</span>}
                      {q.provider_reliability && <span>Reliability: {reliabilityPct(q.provider_reliability)}%</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Timeline */}
        <div className="v2-detail-section">
          <div className="v2-detail-section-header">
            <span className="v2-detail-section-title">Timeline</span>
          </div>
          <div className="v2-detail-timeline">
            {events.length === 0 ? (
              <div style={{ padding: '1rem 0.5rem', fontSize: 13, color: '#8F8F85' }}>
                No events yet. Quote arrivals, bookings, and tracking milestones will appear here.
              </div>
            ) : (
              events.map((ev, idx) => (
                <div key={ev.id} className="v2-timeline-item">
                  <div className="v2-timeline-dot-wrapper">
                    <div className="v2-timeline-dot" />
                    {idx < events.length - 1 && <div className="v2-timeline-connector" />}
                  </div>
                  <div className="v2-timeline-content">
                    <div className="v2-timeline-message">
                      {ev.description || ev.event_type}
                      {ev.location && <span style={{ opacity: 0.7 }}> · {ev.location}</span>}
                    </div>
                    <div className="v2-timeline-time">{formatDateTime(ev.event_date)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function DetailRow({ label, value }) {
  return (
    <div className="v2-detail-row">
      <span className="v2-detail-label">{label}</span>
      <span className="v2-detail-value">{value}</span>
    </div>
  )
}
